import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCenter, setCountry } from '../../../redux/slices/centerSlice';
import { setClassCenters, setLoading, setError } from '../../../redux/slices/centersListSlice';
import { setSelectedCenter } from '../../../redux/slices/classSlice';
import classService from '../services/classService';

const ClassCenter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);
  
  // Redux state
  const { classCenters, loading, error } = useSelector((state) => state.centersList);
  const { selectedCenter } = useSelector((state) => state.class);
  const centers = classCenters;
  const localLoading = loading;
  const localError = error;

  // Fetch centers on mount
  useEffect(() => {
    // Check if data is already cached before making API call
    if (centers.length > 0) {
      return; // Data already loaded, skip API call
    }

    // Check if already fetching
    if (fetchingRef.current) {
      return;
    }

    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    fetchingRef.current = true;
    try {
      dispatch(setLoading(true));
      const data = await classService.getCenters();
      dispatch(setClassCenters(data || []));
      dispatch(setError(null));
    } catch (err) {
      const errorMsg = 'Failed to load centers. Please try again later.';
      dispatch(setError(errorMsg));
      console.error(err);
    } finally {
      dispatch(setLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleSelectCenter = (center) => {
    dispatch(setCenter({
      center_Id: center.center_Id,
      center_Name: center.center_Name,
      center_Logo_URL: center.center_Logo_URL,
      center_Desc: center.center_Desc,
      organization_Id: center.organization_Id,
      app_Id: center.app_Id,
    }));
    
    dispatch(setCountry('USA'));
    
    // Save selected center to Redux
    dispatch(setSelectedCenter(center));
    
    navigate(`/class/category?centerId=${center.center_Id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
              Select Your Class Center
            </h1>
            <p className="text-sm text-gray-500">
              Discover amazing classes at our premium centers
            </p>
          </div>
        </div>

        {localLoading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading centers...</p>
          </div>
        )}

        {localError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{localError}</p>
          </div>
        )}

        {/* Centers Grid */}
        {!localLoading && centers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {centers.map((center) => (
              <div
                key={center.center_Id}
                className={`item-card cursor-pointer ${
                  selectedCenter?.center_Id === center.center_Id ? 'selected' : ''
                }`}
                onClick={() => handleSelectCenter(center)}
              >
                {/* Selection Tick Mark */}
                {selectedCenter?.center_Id === center.center_Id && (
                  <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                    <svg className="w-5 h-5 text-white font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                
                {/* Image Section */}
                {center.center_Logo_URL && (
                  <div className="h-56 bg-gradient-to-br from-blue-400 to-purple-400 overflow-hidden relative rounded-t-[18px]">
                    <img
                      src={center.center_Logo_URL?.trim()}
                      alt={center.center_Name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=Class+Center';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                )}
                
                {/* Content Section */}
                <div className="p-6 relative z-10">
                  <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-2 leading-snug">
                    {center.center_Name}
                  </h3>
                  
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                    {center.center_Desc}
                  </p>
                  
                  <button className="w-full bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-full border border-gray-200 transition shadow-sm hover:shadow-md text-sm">
                    Explore Classes
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!localLoading && centers.length === 0 && !localError && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No centers available for the selected country.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassCenter;
